import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../utils/api';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;

const PaymentSuccess = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get('session_id');

    // 'verifying' (polling), 'confirmed' (success), 'pending' (timeout) or 'error'
    const [state, setState] = useState('verifying');
    const [paymentInfo, setPaymentInfo] = useState(null);
    const cancelledRef = useRef(false);

    useEffect(() => {
        if (!sessionId) {
            setState('error');
            return;
        }
        cancelledRef.current = false;
        pollUntilScheduled();
        return () => { cancelledRef.current = true; };
        // eslint-disable-next-line
    }, [sessionId]);

    const pollUntilScheduled = async () => {
        const deadline = Date.now() + POLL_TIMEOUT_MS;
        while (!cancelledRef.current && Date.now() < deadline) {
            try {
                const res = await api.get(`/payments/status/${sessionId}`);
                setPaymentInfo(res.data);
                if (res.data?.booking_status === 'scheduled') {
                    if (!cancelledRef.current) setState('confirmed');
                    return;
                }
            } catch (e) {
                // Continue polling — transient errors shouldn't end the loop
            }
            await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        }
        if (!cancelledRef.current) setState('pending');
    };

    // --- VERIFYING (loading) ---
    if (state === 'verifying') {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F0FDF4' }}>
                <div className="text-center" data-testid="payment-verifying">
                    <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#10B981] border-t-transparent mx-auto mb-4" />
                    <p className="text-slate-500" style={{ fontFamily: 'Barlow, sans-serif' }}>Verifying your payment…</p>
                    <p className="text-xs text-slate-400 mt-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                        This can take up to 30 seconds.
                    </p>
                </div>
            </div>
        );
    }

    // --- PENDING (timeout reached but not yet scheduled) ---
    if (state === 'pending') {
        return (
            <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F0FDF4' }}>
                <div className="bg-white rounded-3xl shadow-sm border border-amber-200/60 max-w-md w-full p-8 text-center" data-testid="payment-pending">
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#FFFBEB' }}>
                        <Clock className="h-10 w-10 text-amber-500" />
                    </div>
                    <span className="uppercase tracking-[0.2em] text-xs font-semibold text-amber-600 block mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                        Pending Confirmation
                    </span>
                    <h1 className="text-2xl font-bold mb-2 tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>
                        Payment is processing
                    </h1>
                    <p className="text-slate-500 mb-6" style={{ fontFamily: 'Barlow, sans-serif' }}>
                        Your payment is being confirmed by Stripe. The booking will appear as <strong>Scheduled</strong> on
                        your dashboard shortly. You'll also receive an email confirmation.
                    </p>
                    <div className="rounded-2xl p-4 mb-6 text-left border border-amber-200/60 bg-amber-50/50">
                        <div className="flex items-start space-x-3">
                            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-amber-800" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                If the status doesn't update within a few minutes, refresh your dashboard or check the
                                booking details page.
                            </p>
                        </div>
                    </div>
                    <div className="space-y-3">
                        <Button
                            onClick={() => navigate('/company/trials')}
                            className="w-full btn-primary h-12"
                            data-testid="payment-pending-go-to-trials-btn"
                        >
                            Go to My Trials
                            <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => navigate('/company/dashboard')}
                            className="w-full rounded-full border-purple-200 hover:border-purple-400 hover:bg-purple-50"
                            data-testid="payment-pending-back-to-dashboard-btn"
                        >
                            Back to Dashboard
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // --- ERROR (no session id) ---
    if (state === 'error') {
        return (
            <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F0FDF4' }}>
                <div className="bg-white rounded-3xl shadow-sm border border-red-200/60 max-w-md w-full p-8 text-center" data-testid="payment-error">
                    <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>Missing session</h1>
                    <p className="text-slate-500 mb-6">No payment session was found in this URL.</p>
                    <Button onClick={() => navigate('/company/dashboard')} className="btn-primary">
                        Back to Dashboard
                    </Button>
                </div>
            </div>
        );
    }

    // --- CONFIRMED ---
    return (
        <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F0FDF4' }}>
            <div className="bg-white rounded-3xl shadow-sm border border-purple-200/50 max-w-md w-full p-8 text-center" data-testid="payment-confirmed">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#ECFDF5' }}>
                    <CheckCircle className="h-10 w-10 text-[#10B981]" />
                </div>

                <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#10B981] block mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    Confirmed
                </span>
                <h1 className="text-2xl font-bold mb-2 tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>
                    Payment Confirmed
                </h1>
                <p className="text-slate-500 mb-6" style={{ fontFamily: 'Barlow, sans-serif' }}>
                    Your trial shift is now scheduled. The candidate has been notified.
                </p>

                {paymentInfo && (
                    <div className="rounded-2xl p-4 mb-6 text-left border border-purple-200/50" style={{ background: 'linear-gradient(135deg, #1E1B4B 0%, #312E81 50%, #4C1D95 100%)' }}>
                        <div className="flex justify-between text-sm mb-2">
                            <span className="text-purple-200">Amount paid</span>
                            <span className="font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                £{paymentInfo.amount?.toFixed(2)}
                            </span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-purple-200">Status</span>
                            <span className="font-semibold text-[#34D399] capitalize" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                {paymentInfo.booking_status || paymentInfo.payment_status}
                            </span>
                        </div>
                    </div>
                )}

                <div className="space-y-3">
                    <Button
                        onClick={() => navigate('/company/trials')}
                        className="w-full btn-primary h-12"
                        data-testid="go-to-trials-btn"
                    >
                        View My Trials
                        <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => navigate('/company/dashboard')}
                        className="w-full rounded-full border-purple-200 hover:border-purple-400 hover:bg-purple-50"
                        data-testid="back-to-dashboard-btn"
                    >
                        Back to Dashboard
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default PaymentSuccess;
